// ============================================
// GALLERY SCREEN - データコンソール風ギャラリーモード
// ============================================

import { getP5 } from '../../core/p5-instance';
import { CONFIG, ShipConfig } from '../../config/game-config';
import {BaseEnemyConfig, BossEnemyConfig, ENEMY_CONFIG} from '../../config/enemy-config';
import { getTitleTimer, getGalleryCategory, getGalleryIndex, getGalleryStartTime, getGalleryLoadingTimer, getGalleryShipPage } from '../../store/index';
import type { Enemy, EnemyType, GalleryCategory, ShipType } from '../../types/game-types';
import { drawShipBomber, drawShipHunter, drawShipLancer } from '../../features/player/renderer/ship-renderer';
import {
    drawNormalEnemy,
    drawTankEnemy,
    drawFastEnemy,
    drawArmoredEnemy,
    drawSpeedyEnemy,
    drawLaserEnemy,
    drawMirrorEnemy,
    drawBossEnemy,
    getDamageColor
} from '../../features/enemy/renderer/enemy-renderer';

// コンソールカラーパレット
const CONSOLE_COLORS = {
    primary: [0, 255, 0],       // 緑（クラシックターミナル）
    secondary: [0, 255, 255],   // シアン（データハイライト）
    warning: [255, 255, 0],     // 黄色（重要情報）
    error: [255, 0, 0],         // 赤（警告）
    bg: [0, 17, 0],             // 濃い緑黒
    grid: [0, 80, 0],           // グリッド線
    text: [0, 220, 0]           // テキスト
};

// ============================================
// drawGalleryScreen() - ギャラリー画面の描画
// ============================================
export function drawGalleryScreen() {
    const p = getP5();
    const category = getGalleryCategory();
    const index = getGalleryIndex();
    const loadingTimer = getGalleryLoadingTimer();

    p.textFont('Orbitron');

    // 画面起動アニメーション（フェードイン + グリッド展開）
    const startTime = getGalleryStartTime();
    const elapsed = getTitleTimer() - startTime;
    const fadeInDuration = 60; // 1秒
    const fadeProgress = elapsed < fadeInDuration ? elapsed / fadeInDuration : 1.0;

    // コンソール背景エフェクト（グリッド展開アニメーション）
    drawConsoleBackground(fadeProgress);

    // フェードイン適用
    (p.drawingContext as CanvasRenderingContext2D).globalAlpha = fadeProgress;

    // ローディング中の場合
    if (loadingTimer > 0) {
        drawLoadingScreen(category);
        (p.drawingContext as CanvasRenderingContext2D).globalAlpha = 1.0;
        drawScanlineEffect();
        return;
    }

    // ターミナルヘッダー
    drawConsoleHeader(category);

    // カテゴリタブ（ターミナルコマンド風）
    drawConsoleTabs(category);

    // データフレーム
    drawDataFrame();

    // コンテンツ表示
    if (category === 'ships') {
        drawShipsGallery(index);
    } else if (category === 'enemies') {
        drawEnemiesGallery(index);
    } else if (category === 'stages') {
        drawStagesGallery(index);
    }

    // ナビゲーション（ターミナルボタン風）
    drawNavigationButtons(category, index);

    // 終了ボタン
    drawExitButton();

    (p.drawingContext as CanvasRenderingContext2D).globalAlpha = 1.0;

    // スキャンライン効果（最前面）
    drawScanlineEffect();
}

// ============================================
// drawConsoleBackground() - コンソール背景エフェクト
// ============================================
function drawConsoleBackground(fadeProgress: number) {
    const p = getP5();
    // 背景色（濃い緑黒）
    p.fill(CONSOLE_COLORS.bg[0], CONSOLE_COLORS.bg[1], CONSOLE_COLORS.bg[2], 250);
    p.rect(0, 0, p.width, p.height);

    // グリッド線（展開アニメーション）
    p.stroke(CONSOLE_COLORS.grid[0], CONSOLE_COLORS.grid[1], CONSOLE_COLORS.grid[2], 30 * fadeProgress);
    p.strokeWeight(1);
    const gridSize = 20;

    // グリッド展開エフェクト（中心から外側へ）
    const centerX = p.width / 2;
    const centerY = p.height / 2;
    const maxDist = Math.sqrt(centerX * centerX + centerY * centerY);

    // 縦線
    for (let x = 0; x < p.width; x += gridSize) {
        const dist = Math.abs(x - centerX);
        const lineProgress = p.constrain((fadeProgress * maxDist - dist) / 50, 0, 1);
        if (lineProgress > 0) {
            p.stroke(CONSOLE_COLORS.grid[0], CONSOLE_COLORS.grid[1], CONSOLE_COLORS.grid[2], 30 * lineProgress);
            p.line(x, 0, x, p.height);
        }
    }

    // 横線
    for (let y = 0; y < p.height; y += gridSize) {
        const dist = Math.abs(y - centerY);
        const lineProgress = p.constrain((fadeProgress * maxDist - dist) / 50, 0, 1);
        if (lineProgress > 0) {
            p.stroke(CONSOLE_COLORS.grid[0], CONSOLE_COLORS.grid[1], CONSOLE_COLORS.grid[2], 30 * lineProgress);
            p.line(0, y, p.width, y);
        }
    }

    // ビネット効果（画面端を暗く）
    p.noStroke();
    const vignetteSize = 80;
    for (let i = 0; i < vignetteSize; i++) {
        const alpha = (vignetteSize - i) / vignetteSize * 60;
        p.fill(0, 0, 0, alpha);
        p.noStroke();
        // 上下
        p.rect(0, i, p.width, 1);
        p.rect(0, p.height - i, p.width, 1);
        // 左右
        p.rect(i, 0, 1, p.height);
        p.rect(p.width - i, 0, 1, p.height);
    }

    // ランダムノイズ（データ転送中演出）
    for (let i = 0; i < 20; i++) {
        p.fill(CONSOLE_COLORS.primary[0], CONSOLE_COLORS.primary[1], CONSOLE_COLORS.primary[2], p.random(10, 40));
        const nx = p.random(p.width);
        const ny = p.random(p.height);
        p.rect(nx, ny, p.random(1, 3), p.random(1, 3));
    }
}

// ============================================
// drawLoadingScreen() - ローディング画面
// ============================================
function drawLoadingScreen(category: GalleryCategory) {
    const p = getP5();
    const categoryNames: Record<GalleryCategory, string> = {
        ships: 'SHIP_DATABASE',
        enemies: 'THREAT_ANALYSIS',
        stages: 'SECTOR_MAPS'
    };

    p.textAlign(p.CENTER, p.CENTER);
    p.textSize(20);

    // LOADINGテキスト（点滅）
    const blink = p.floor(getTitleTimer() / 10) % 2 === 0;
    if (blink) {
        p.fill(CONSOLE_COLORS.secondary);
        p.text('LOADING ' + categoryNames[category] + '...', p.width / 2, p.height / 2 - 20);
    }

    // プログレスバー
    const loadingTimer = getGalleryLoadingTimer();
    const progress = 1.0 - (loadingTimer / 30);

    const barW = 300;
    const barH = 20;
    const barX = p.width / 2 - barW / 2;
    const barY = p.height / 2 + 20;

    // 枠
    p.noFill();
    p.stroke(CONSOLE_COLORS.secondary);
    p.strokeWeight(2);
    p.rect(barX, barY, barW, barH);

    // バー
    p.fill(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 150);
    p.noStroke();
    p.rect(barX + 2, barY + 2, (barW - 4) * progress, barH - 4);

    // グリッド
    p.stroke(CONSOLE_COLORS.secondary[0] * 0.5, CONSOLE_COLORS.secondary[1] * 0.5, CONSOLE_COLORS.secondary[2] * 0.5);
    p.strokeWeight(1);
    for (let i = 1; i < 10; i++) {
        const gx = barX + (barW / 10) * i;
        p.line(gx, barY, gx, barY + barH);
    }

    // パーセント表示
    p.textSize(12);
    p.fill(CONSOLE_COLORS.text);
    p.text(p.floor(progress * 100) + '%', p.width / 2, barY + barH + 25);
}

// ============================================
// drawScanlineEffect() - スキャンライン効果
// ============================================
function drawScanlineEffect() {
    const p = getP5();
    // 流れるスキャンライン
    const scanSpeed = 2;
    const scanY = (getTitleTimer() * scanSpeed) % p.height;

    p.stroke(CONSOLE_COLORS.primary[0], CONSOLE_COLORS.primary[1], CONSOLE_COLORS.primary[2], 60);
    p.strokeWeight(2);
    p.line(0, scanY, p.width, scanY);

    p.stroke(CONSOLE_COLORS.primary[0], CONSOLE_COLORS.primary[1], CONSOLE_COLORS.primary[2], 30);
    p.strokeWeight(1);
    p.line(0, scanY + 2, p.width, scanY + 2);
}

// ============================================
// drawConsoleHeader() - ターミナルヘッダー
// ============================================
function drawConsoleHeader(category: GalleryCategory) {
    const p = getP5();
    p.textAlign(p.LEFT, p.TOP);
    p.textSize(10);
    p.noStroke();

    const categoryNames: Record<GalleryCategory, string> = {
        ships: 'SHIP_DATABASE',
        enemies: 'THREAT_ANALYSIS',
        stages: 'SECTOR_MAPS'
    };

    // ターミナル風テキスト
    p.fill(CONSOLE_COLORS.primary);
    p.text('> ACCESSING DATABANK...', 15, 15);

    p.fill(CONSOLE_COLORS.secondary);
    p.text('> CLEARANCE: GRANTED', 15, 28);

    p.fill(CONSOLE_COLORS.warning);
    p.text('> MODULE: ' + categoryNames[category], 15, 41);

    // タイムスタンプ
    p.textAlign(p.RIGHT, p.TOP);
    p.fill(CONSOLE_COLORS.text[0], CONSOLE_COLORS.text[1], CONSOLE_COLORS.text[2], 150);
    const timestamp = p.floor(p.millis() / 1000);
    p.text('[SYS_TIME: ' + timestamp + ']', p.width - 15, 15);

    // 点滅カーソル
    const blink = p.sin(getTitleTimer() * 0.15) > 0;
    if (blink) {
        p.textAlign(p.LEFT, p.TOP);
        const moduleText = '> MODULE: ' + categoryNames[category];
        const textW = p.textWidth(moduleText);
        p.fill(CONSOLE_COLORS.primary);
        p.rect(15 + textW + 2, 41, 8, 10);
    }
}

// ============================================
// drawConsoleTabs() - カテゴリタブ（ターミナル風）
// ============================================
function drawConsoleTabs(currentCategory: GalleryCategory) {
    const p = getP5();
    const categories = ['ships', 'enemies', 'stages'];
    const labels = ['[ SHIPS ]', '[ ENEMIES ]', '[ STAGES ]'];
    const tabY = 70;
    const tabSpacing = 110;
    const startX = 35;

    p.textAlign(p.LEFT, p.CENTER);
    p.textSize(12);

    for (let i = 0; i < categories.length; i++) {
        const tabX = startX + i * tabSpacing;
        const isActive = categories[i] === currentCategory;

        // 選択中のマーカー
        if (isActive) {
            p.fill(CONSOLE_COLORS.secondary);
            p.text('>>>', tabX - 20, tabY);
        }

        // タブテキスト
        if (isActive) {
            p.fill(CONSOLE_COLORS.secondary);
            // 点滅エフェクト
            const flicker = p.sin(getTitleTimer() * 0.2) * 0.2 + 0.8;
            p.fill(CONSOLE_COLORS.secondary[0] * flicker, CONSOLE_COLORS.secondary[1] * flicker, CONSOLE_COLORS.secondary[2] * flicker);
        } else {
            p.fill(CONSOLE_COLORS.primary[0] * 0.5, CONSOLE_COLORS.primary[1] * 0.5, CONSOLE_COLORS.primary[2] * 0.5);
        }
        p.text(labels[i], tabX, tabY);
    }
}

// ============================================
// drawDataFrame() - データ表示エリアの枠
// ============================================
function drawDataFrame() {
    const p = getP5();
    const frameX = 30;
    const frameY = 120;
    const frameW = p.width - 60;
    const frameH = p.height - 240;

    // 点線の枠
    p.noFill();
    p.stroke(CONSOLE_COLORS.primary[0], CONSOLE_COLORS.primary[1], CONSOLE_COLORS.primary[2], 150);
    p.strokeWeight(1);
    const ctx = p.drawingContext as CanvasRenderingContext2D;
    if (ctx.setLineDash) {
        ctx.setLineDash([5, 5]);
    }
    p.rect(frameX, frameY, frameW, frameH);
    if (ctx.setLineDash) {
        ctx.setLineDash([]);
    }

    // 4隅のL字マーカー
    const cornerSize = 15;
    p.stroke(CONSOLE_COLORS.secondary);
    p.strokeWeight(2);

    // 左上
    p.line(frameX, frameY, frameX + cornerSize, frameY);
    p.line(frameX, frameY, frameX, frameY + cornerSize);

    // 右上
    p.line(frameX + frameW, frameY, frameX + frameW - cornerSize, frameY);
    p.line(frameX + frameW, frameY, frameX + frameW, frameY + cornerSize);

    // 左下
    p.line(frameX, frameY + frameH, frameX + cornerSize, frameY + frameH);
    p.line(frameX, frameY + frameH, frameX, frameY + frameH - cornerSize);

    // 右下
    p.line(frameX + frameW, frameY + frameH, frameX + frameW - cornerSize, frameY + frameH);
    p.line(frameX + frameW, frameY + frameH, frameX + frameW, frameY + frameH - cornerSize);

    // スキャンバー（上から下へ移動）
    const scanSpeed = 0.5;
    const scanProgress = (getTitleTimer() * scanSpeed) % 1.0;
    const scanBarY = frameY + frameH * scanProgress;

    p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 80);
    p.strokeWeight(1);
    p.line(frameX, scanBarY, frameX + frameW, scanBarY);
}

// ============================================
// drawShipsGallery() - 機体ギャラリーの描画
// ============================================
function drawShipsGallery(index: number) {
    const p = getP5();
    const ships: ShipType[] = ['bomber', 'hunter', 'lancer'];
    const shipName = ships[index];
    const shipCfg = CONFIG.ships[shipName];
    const currentPage = getGalleryShipPage();

    // 機体名（ターミナル風）
    p.textAlign(p.CENTER, p.TOP);
    p.textSize(18);
    p.fill(CONSOLE_COLORS.secondary);
    p.text('[ ' + shipCfg.name + ' ]', p.width / 2, 135);

    // ID番号（ターミナル風）
    p.textSize(10);
    p.fill(CONSOLE_COLORS.text[0], CONSOLE_COLORS.text[1], CONSOLE_COLORS.text[2], 150);
    p.text('UNIT_ID: 0x' + (index + 1).toString(16).toUpperCase().padStart(4, '0'), p.width / 2, 155);

    // ページタブ表示
    drawShipPageTabs(currentPage);

    // ページ別コンテンツ表示
    if (currentPage === 0) {
        drawShipInfoPage(shipName, shipCfg, index);
    } else if (currentPage === 1) {
        drawShipSpecPage(shipName, shipCfg, index);
    } else if (currentPage === 2) {
        drawShipAbilityPage(shipName, shipCfg, index);
    }

    // インジケーター
    p.textAlign(p.CENTER, p.TOP);
    p.fill(CONSOLE_COLORS.text[0], CONSOLE_COLORS.text[1], CONSOLE_COLORS.text[2], 150);
    p.textSize(10);
    p.text('[RECORD ' + (index + 1) + '/' + ships.length + ']', p.width / 2, p.height - 105);
}

// ============================================
// drawShipPageTabs() - ページタブ表示
// ============================================
function drawShipPageTabs(currentPage: number) {
    const p = getP5();
    const tabY = 180;
    const tabW = 70;
    const tabH = 25;
    const tabSpacing = 8;
    const centerX = p.width / 2;

    p.textAlign(p.CENTER, p.CENTER);
    p.textSize(10);

    // [INFO] タブ
    const infoX = centerX - tabW - tabSpacing;
    const isInfoActive = currentPage === 0;

    if (isInfoActive) {
        p.fill(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 100);
        p.stroke(CONSOLE_COLORS.secondary);
    } else {
        p.fill(CONSOLE_COLORS.primary[0] * 0.3, CONSOLE_COLORS.primary[1] * 0.3, CONSOLE_COLORS.primary[2] * 0.3, 50);
        p.stroke(CONSOLE_COLORS.primary[0] * 0.5, CONSOLE_COLORS.primary[1] * 0.5, CONSOLE_COLORS.primary[2] * 0.5);
    }
    p.strokeWeight(1);
    p.rect(infoX - tabW / 2, tabY - tabH / 2, tabW, tabH, 3);

    p.fill(isInfoActive ? CONSOLE_COLORS.secondary : CONSOLE_COLORS.primary);
    p.noStroke();
    p.text('[INFO]', infoX, tabY);

    // [SPEC] タブ
    const specX = centerX;
    const isSpecActive = currentPage === 1;

    if (isSpecActive) {
        p.fill(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 100);
        p.stroke(CONSOLE_COLORS.secondary);
    } else {
        p.fill(CONSOLE_COLORS.primary[0] * 0.3, CONSOLE_COLORS.primary[1] * 0.3, CONSOLE_COLORS.primary[2] * 0.3, 50);
        p.stroke(CONSOLE_COLORS.primary[0] * 0.5, CONSOLE_COLORS.primary[1] * 0.5, CONSOLE_COLORS.primary[2] * 0.5);
    }
    p.strokeWeight(1);
    p.rect(specX - tabW / 2, tabY - tabH / 2, tabW, tabH, 3);

    p.fill(isSpecActive ? CONSOLE_COLORS.secondary : CONSOLE_COLORS.primary);
    p.noStroke();
    p.text('[SPEC]', specX, tabY);

    // [ABILITY] タブ
    const abilityX = centerX + tabW + tabSpacing;
    const isAbilityActive = currentPage === 2;

    if (isAbilityActive) {
        p.fill(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 100);
        p.stroke(CONSOLE_COLORS.secondary);
    } else {
        p.fill(CONSOLE_COLORS.primary[0] * 0.3, CONSOLE_COLORS.primary[1] * 0.3, CONSOLE_COLORS.primary[2] * 0.3, 50);
        p.stroke(CONSOLE_COLORS.primary[0] * 0.5, CONSOLE_COLORS.primary[1] * 0.5, CONSOLE_COLORS.primary[2] * 0.5);
    }
    p.strokeWeight(1);
    p.rect(abilityX - tabW / 2, tabY - tabH / 2, tabW, tabH, 3);

    p.fill(isAbilityActive ? CONSOLE_COLORS.secondary : CONSOLE_COLORS.primary);
    p.noStroke();
    p.text('[ABILITY]', abilityX, tabY);
}

// ============================================
// drawShipInfoPage() - 機体情報ページ（世界観）
// ============================================
function drawShipInfoPage(shipName: string, _shipCfg: ShipConfig, __index: number) {
    const p = getP5();
    // 機体描画（ホログラム風）
    p.push();
    p.translate(p.width / 2, 270);
    const size = 80;

    // ホログラムエフェクト（透明度変動）
    const hologramAlpha = 0.8 + p.sin(getTitleTimer() * 0.1) * 0.2;
    (p.drawingContext as CanvasRenderingContext2D).globalAlpha = hologramAlpha;

    if (shipName === 'bomber') {
        drawShipBomber(0, 0, size, {});
    } else if (shipName === 'hunter') {
        drawShipHunter(0, 0, size, {});
    } else if (shipName === 'lancer') {
        drawShipLancer(0, 0, size, {});
    }

    (p.drawingContext as CanvasRenderingContext2D).globalAlpha = 1.0;

    // 計測点マーカー
    p.noFill();
    p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 100);
    p.strokeWeight(1);
    p.circle(0, 0, size * 1.5);

    // 十字線
    p.line(-size, 0, -size * 0.6, 0);
    p.line(size * 0.6, 0, size, 0);
    p.line(0, -size, 0, -size * 0.6);
    p.line(0, size * 0.6, 0, size);

    p.pop();

    // 説明
    p.textAlign(p.CENTER, p.TOP);
    p.textSize(11);
    p.fill(CONSOLE_COLORS.warning);
    // p.text('// ' + shipCfg.description, p.width / 2, 350);

    // 世界観テキスト
    const loreTexts: Record<string, string[]> = {
        bomber: [
            'STANDARD ASSAULT FIGHTER',
            'Balanced performance for all missions.',
            'Equipped with area-denial bomb system.'
        ],
        hunter: [
            'HIGH-SPEED INTERCEPTOR',
            'Superior maneuverability and evasion.',
            'Homing missile system for pursuit.'
        ],
        lancer: [
            'HEAVY ASSAULT STRIKER',
            'Maximum firepower with charged beam.',
            'Requires skilled pilot operation.'
        ]
    };

    const lore = loreTexts[shipName] || [];
    p.textSize(10);
    p.fill(CONSOLE_COLORS.text);
    const loreY = 380;
    for (let i = 0; i < lore.length; i++) {
        p.text(lore[i], p.width / 2, loreY + i * 18);
    }
}

// ============================================
// drawShipSpecPage() - 機体スペックページ（ゲームパラメータ）
// ============================================
function drawShipSpecPage(_shipName: string, shipCfg: ShipConfig, __index: number) {
    const p = getP5();
    const stats = shipCfg.stats;
    const rankLabels = ['D', 'C', 'B', 'A', 'S'];

    // ステータスバー表示
    const startY = 220;
    const lineH = 30;
    const labelX = 50;
    const barX = 160;
    const barW = 150;
    const barH = 16;

    p.textAlign(p.LEFT, p.CENTER);
    p.textSize(11);

    // POWER
    p.fill(CONSOLE_COLORS.text);
    p.text('POWER:', labelX, startY);
    drawStatBar(barX, startY - barH / 2, barW, barH, stats.power, CONSOLE_COLORS.warning);
    p.fill(CONSOLE_COLORS.warning);
    p.textAlign(p.RIGHT, p.CENTER);
    p.text('[' + rankLabels[stats.power - 1] + ']', barX + barW + 50, startY);

    // MOBILITY
    p.textAlign(p.LEFT, p.CENTER);
    p.fill(CONSOLE_COLORS.text);
    p.text('MOBILITY:', labelX, startY + lineH);
    drawStatBar(barX, startY + lineH - barH / 2, barW, barH, stats.mobility, CONSOLE_COLORS.secondary);
    p.fill(CONSOLE_COLORS.secondary);
    p.textAlign(p.RIGHT, p.CENTER);
    p.text('[' + rankLabels[stats.mobility - 1] + ']', barX + barW + 50, startY + lineH);

    // SHIELD
    p.textAlign(p.LEFT, p.CENTER);
    p.fill(CONSOLE_COLORS.text);
    p.text('SHIELD:', labelX, startY + lineH * 2);
    drawStatBar(barX, startY + lineH * 2 - barH / 2, barW, barH, stats.shield, CONSOLE_COLORS.secondary);
    p.fill(CONSOLE_COLORS.secondary);
    p.textAlign(p.RIGHT, p.CENTER);
    p.text('[' + rankLabels[stats.shield - 1] + ']', barX + barW + 50, startY + lineH * 2);

    // STABILITY
    p.textAlign(p.LEFT, p.CENTER);
    p.fill(CONSOLE_COLORS.text);
    p.text('STABILITY:', labelX, startY + lineH * 3);
    drawStatBar(barX, startY + lineH * 3 - barH / 2, barW, barH, stats.stability, CONSOLE_COLORS.secondary);
    p.fill(CONSOLE_COLORS.secondary);
    p.textAlign(p.RIGHT, p.CENTER);
    p.text('[' + rankLabels[stats.stability - 1] + ']', barX + barW + 50, startY + lineH * 3);
}

// ============================================
// drawShipAbilityPage() - アビリティ詳細ページ
// ============================================
function drawShipAbilityPage(shipName: string, shipCfg: ShipConfig, __index: number) {
    const startY = 215;
    const abilityNames: Record<string, string> = { bomb: 'ボム', homing: 'ホーミング', cannon: '荷電粒子砲' };

    // 通常兵装データ
    const normalWeaponData: Record<string, { type: string; description: string[] }> = {
        bomber: {
            type: '拡散ショット',
            description: [
                'パワーレベルに応じて弾数が増加',
                '15度の間隔で均等に拡散配置される'
            ]
        },
        hunter: {
            type: '高速ショット',
            description: [
                'パワーレベルに応じて弾数が増加（Lv1: 1発 ～ Lv4: 4発）',
                '軽量で速度重視の弾丸'
            ]
        },
        lancer: {
            type: '貫通レーザー',
            description: [
                'パワーレベルに応じてレーザー幅が増加',
                '複数の敵を貫通してダメージを与え続ける',
                'ヒット間隔はパワーレベルで短縮'
            ]
        }
    };

    // アビリティ詳細説明データ
    const abilityData: Record<string, { effect: string[]; levelUp: string[] }> = {
        bomber: {
            effect: [
                '一定時間の間、画面上のすべての敵に連続でダメージを与える。',
                '',
                '効果発動中は敵弾も消滅させる。'
            ],
            levelUp: [
                '効果時間延長。ダメージ間隔短縮。'
            ]
        },
        hunter: {
            effect: [
                'ホーミング弾モードを10秒間発動します。',
                '',
                'すべての弾が最も近い敵を自動追尾します。'
            ],
            levelUp: [
                '弾速アップ、追尾性能向上、射撃パワー+1。'
            ]
        },
        lancer: {
            effect: [
                '荷電粒子砲を発射します（チャージ0.7秒）。',
                '',
                '発動中は連続ダメージを与え続けます。'
            ],
            levelUp: [
                'チャージ時間短縮、効果時間延長、威力向上。'
            ]
        }
    };

    const data = abilityData[shipName];
    const normalWeapon = normalWeaponData[shipName];

    // 【大項目1】通常兵装カード
    let currentY = startY;
    drawNormalWeaponCard(currentY, normalWeapon.type, normalWeapon.description, shipName);

    // 【大項目2】特殊兵装カード
    currentY += 120;
    drawMajorSectionCard(
        currentY,
        '特殊兵装',
        abilityNames[shipCfg.ability],
        [
            { title: '性能', lines: data.effect },
            { title: 'パワーアップによる効果', lines: data.levelUp }
        ],
        shipName
    );

    // 【大項目3】機体性能カード
    currentY += 120;
    drawMajorPerformanceCard(shipCfg, currentY, shipName);
}

// ============================================
// drawNormalWeaponCard() - 通常兵装カード（新レイアウト）
// ============================================
function drawNormalWeaponCard(y: number, weaponType: string, description: string[], shipName: string) {
    const p = getP5();
    const cardX = 40;
    const cardW = p.width - 80;
    const cardH = 110;

    // 外側のカード枠
    p.fill(CONSOLE_COLORS.primary[0] * 0.15, CONSOLE_COLORS.primary[1] * 0.15, CONSOLE_COLORS.primary[2] * 0.15, 130);
    p.stroke(CONSOLE_COLORS.primary[0], CONSOLE_COLORS.primary[1], CONSOLE_COLORS.primary[2], 120);
    p.strokeWeight(1);
    p.rect(cardX, y, cardW, cardH, 4);

    // コーナーグロー
    const cornerSize = 8;
    const glowPulse = p.sin(getTitleTimer() * 0.12) * 0.4 + 0.6;
    p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 120 * glowPulse);
    p.strokeWeight(2);
    p.line(cardX, y, cardX + cornerSize, y);
    p.line(cardX, y, cardX, y + cornerSize);
    p.line(cardX + cardW, y, cardX + cardW - cornerSize, y);
    p.line(cardX + cardW, y, cardX + cardW, y + cornerSize);
    p.line(cardX, y + cardH, cardX + cornerSize, y + cardH);
    p.line(cardX, y + cardH, cardX, y + cardH - cornerSize);
    p.line(cardX + cardW, y + cardH, cardX + cardW - cornerSize, y + cardH);
    p.line(cardX + cardW, y + cardH, cardX + cardW, y + cardH - cornerSize);

    // スキャンライン効果
    drawCardScanline(cardX, y, cardW, cardH);

    // 大項目タイトル
    p.noStroke();
    p.fill(CONSOLE_COLORS.warning);
    p.textSize(12);
    p.textAlign(p.LEFT, p.TOP);
    p.text('■ 通常兵装', cardX + 15, y + 12);

    // サブタイトル（武器タイプ）- 同じ横軸
    p.fill(CONSOLE_COLORS.secondary);
    p.textSize(11);
    p.textAlign(p.LEFT, p.TOP);
    p.text('▶ ' + weaponType, cardX + 100, y + 12);

    // 左側：カプセル状のアイコンエリア
    const capsuleX = cardX + 10;
    const capsuleY = y + 32;
    const capsuleW = 70;
    const capsuleH = 65;
    const capsuleRadius = capsuleH / 2;

    // カプセル背景（両端が丸い）
    p.fill(CONSOLE_COLORS.primary[0] * 0.2, CONSOLE_COLORS.primary[1] * 0.2, CONSOLE_COLORS.primary[2] * 0.2, 150);
    p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 100);
    p.strokeWeight(1);
    p.rect(capsuleX, capsuleY, capsuleW, capsuleH, capsuleRadius);

    // 内側の円（アイコンエリア）
    const circleX = capsuleX + capsuleW / 2;
    const circleY = capsuleY + capsuleH / 2;
    const circleR = 25;

    // 円の背景
    p.fill(CONSOLE_COLORS.primary[0] * 0.3, CONSOLE_COLORS.primary[1] * 0.3, CONSOLE_COLORS.primary[2] * 0.3, 180);
    p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 130);
    p.strokeWeight(1.5);
    p.circle(circleX, circleY, circleR * 2);

    // グロー効果
    p.noFill();
    p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 60 * glowPulse);
    p.strokeWeight(1);
    p.circle(circleX, circleY, (circleR + 4) * 2);

    // アイコンを描画（縮小）
    drawWeaponIcon(circleX, circleY, shipName, 0.7);

    // 右側：説明カード
    const descX = cardX + 90;
    const descY = y + 32;
    const descW = cardW - 100;
    const descH = 65;

    // 説明カードの背景（シンプルな単線）
    p.fill(CONSOLE_COLORS.primary[0] * 0.25, CONSOLE_COLORS.primary[1] * 0.25, CONSOLE_COLORS.primary[2] * 0.25, 140);
    p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 80);
    p.strokeWeight(1);
    p.rect(descX, descY, descW, descH, 3);

    // 説明文（箇条書き）
    p.fill(CONSOLE_COLORS.text);
    p.textSize(8);
    p.textAlign(p.LEFT, p.TOP);
    let textY = descY + 10;
    for (let i = 0; i < description.length; i++) {
        if (description[i].trim() !== '') {
            p.text('• ' + description[i], descX + 8, textY);
            textY += 13;
        }
    }
}

// ============================================
// drawWeaponIcon() - 武器アイコン描画
// ============================================
function drawWeaponIcon(x: number, y: number, shipName: string, iconScale: number = 1.0) {
    const p = getP5();
    p.push();
    p.translate(x, y);
    p.scale(iconScale);
    p.translate(-x, -y);

    p.noStroke();

    if (shipName === 'bomber') {
        // 拡散ショット: 3本の弾が広がる形
        p.fill(CONSOLE_COLORS.secondary);

        // 中央の弾
        p.rect(x - 2, y - 15, 4, 12, 2);

        // 左の弾（斜め）
        p.push();
        p.translate(x - 10, y - 10);
        p.rotate(-0.3);
        p.rect(-2, -10, 4, 12, 2);
        p.pop();

        // 右の弾（斜め）
        p.push();
        p.translate(x + 10, y - 10);
        p.rotate(0.3);
        p.rect(-2, -10, 4, 12, 2);
        p.pop();

        // 発射元
        p.fill(CONSOLE_COLORS.warning);
        p.triangle(x - 8, y + 5, x + 8, y + 5, x, y - 5);

    } else if (shipName === 'hunter') {
        // 高速ショット: 流線形の弾
        p.fill(CONSOLE_COLORS.secondary);

        // 速度を表す線
        p.strokeWeight(2);
        p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 100);
        p.line(x, y - 18, x, y - 8);
        p.line(x - 6, y - 15, x - 6, y - 5);
        p.line(x + 6, y - 15, x + 6, y - 5);

        p.noStroke();
        // 弾
        p.ellipse(x, y - 10, 6, 14);

        // 発射元
        p.fill(CONSOLE_COLORS.warning);
        p.triangle(x - 6, y + 5, x + 6, y + 5, x, y - 3);

    } else if (shipName === 'lancer') {
        // 貫通レーザー: 太い光線
        p.fill(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 180);
        p.rect(x - 8, y - 20, 16, 25, 2);

        // 内側の明るい部分
        p.fill(CONSOLE_COLORS.warning[0], CONSOLE_COLORS.warning[1], CONSOLE_COLORS.warning[2], 200);
        p.rect(x - 4, y - 20, 8, 25, 1);

        // グロー
        p.fill(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 80);
        p.rect(x - 10, y - 20, 20, 25);

        // 発射元
        p.fill(CONSOLE_COLORS.warning);
        p.rect(x - 10, y + 5, 20, 8, 2);
    }

    p.pop();
}

// ============================================
// drawMajorSectionCard() - 大項目カード（特殊兵装）
// ============================================
function drawMajorSectionCard(y: number, majorTitle: string, subTitle: string, sections: { title: string; lines: string[] }[], shipName: string) {
    const p = getP5();
    const cardX = 40;
    const cardW = p.width - 80;
    const cardH = 110;

    // 外側のカード枠
    p.fill(CONSOLE_COLORS.primary[0] * 0.15, CONSOLE_COLORS.primary[1] * 0.15, CONSOLE_COLORS.primary[2] * 0.15, 130);
    p.stroke(CONSOLE_COLORS.primary[0], CONSOLE_COLORS.primary[1], CONSOLE_COLORS.primary[2], 120);
    p.strokeWeight(1);
    p.rect(cardX, y, cardW, cardH, 4);

    // コーナーグロー
    const cornerSize = 8;
    const glowPulse = p.sin(getTitleTimer() * 0.12) * 0.4 + 0.6;
    p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 120 * glowPulse);
    p.strokeWeight(2);
    p.line(cardX, y, cardX + cornerSize, y);
    p.line(cardX, y, cardX, y + cornerSize);
    p.line(cardX + cardW, y, cardX + cardW - cornerSize, y);
    p.line(cardX + cardW, y, cardX + cardW, y + cornerSize);
    p.line(cardX, y + cardH, cardX + cornerSize, y + cardH);
    p.line(cardX, y + cardH, cardX, y + cardH - cornerSize);
    p.line(cardX + cardW, y + cardH, cardX + cardW - cornerSize, y + cardH);
    p.line(cardX + cardW, y + cardH, cardX + cardW, y + cardH - cornerSize);

    // スキャンライン効果
    drawCardScanline(cardX, y, cardW, cardH);

    // 大項目タイトル
    p.noStroke();
    p.fill(CONSOLE_COLORS.warning);
    p.textSize(12);
    p.textAlign(p.LEFT, p.TOP);
    p.text('■ ' + majorTitle, cardX + 15, y + 12);

    // サブタイトル（アビリティ名）- 同じ横軸
    p.fill(CONSOLE_COLORS.secondary);
    p.textSize(11);
    p.textAlign(p.LEFT, p.TOP);
    p.text('▶ ' + subTitle, cardX + 100, y + 12);

    // 左側：カプセル状のアイコンエリア
    const capsuleX = cardX + 10;
    const capsuleY = y + 32;
    const capsuleW = 70;
    const capsuleH = 65;
    const capsuleRadius = capsuleH / 2;

    // カプセル背景（両端が丸い）
    p.fill(CONSOLE_COLORS.primary[0] * 0.2, CONSOLE_COLORS.primary[1] * 0.2, CONSOLE_COLORS.primary[2] * 0.2, 150);
    p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 100);
    p.strokeWeight(1);
    p.rect(capsuleX, capsuleY, capsuleW, capsuleH, capsuleRadius);

    // 内側の円（アイコンエリア）
    const circleX = capsuleX + capsuleW / 2;
    const circleY = capsuleY + capsuleH / 2;
    const circleR = 25;

    // 円の背景
    p.fill(CONSOLE_COLORS.primary[0] * 0.3, CONSOLE_COLORS.primary[1] * 0.3, CONSOLE_COLORS.primary[2] * 0.3, 180);
    p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 130);
    p.strokeWeight(1.5);
    p.circle(circleX, circleY, circleR * 2);

    // グロー効果
    p.noFill();
    p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 60 * glowPulse);
    p.strokeWeight(1);
    p.circle(circleX, circleY, (circleR + 4) * 2);

    // アイコンを描画
    drawSpecialWeaponIcon(circleX, circleY, shipName, 0.7);

    // 右側：説明カード
    const descX = cardX + 90;
    const descY = y + 32;
    const descW = cardW - 100;
    const descH = 65;

    // 説明カードの背景（シンプルな単線）
    p.fill(CONSOLE_COLORS.primary[0] * 0.25, CONSOLE_COLORS.primary[1] * 0.25, CONSOLE_COLORS.primary[2] * 0.25, 140);
    p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 80);
    p.strokeWeight(1);
    p.rect(descX, descY, descW, descH, 3);

    // セクション描画（右カード内）
    let sectionY = descY + 5;
    for (let i = 0; i < sections.length; i++) {
        drawSubSectionCompact(descX + 8, sectionY, descW - 16, sections[i].title, sections[i].lines);
        sectionY += 36;
    }
}


// ============================================
// drawSubSectionCompact() - コンパクトな小項目セクション
// ============================================
function drawSubSectionCompact(x: number, y: number, _w: number, title: string, lines: string[]) {
    const p = getP5();
    // セクションタイトル
    p.noStroke();
    p.fill(CONSOLE_COLORS.secondary);
    p.textSize(8);
    p.textAlign(p.LEFT, p.TOP);
    p.text('▸ ' + title, x, y);

    // 内容（箇条書き）
    p.fill(CONSOLE_COLORS.text);
    p.textSize(7);
    p.textAlign(p.LEFT, p.TOP);
    let textY = y + 12;
    for (let i = 0; i < lines.length; i++) {
        if (lines[i].trim() !== '') {
            p.text('• ' + lines[i], x + 6, textY);
            textY += 5;
        } else {
            textY += 5;
        }
    }
}

// ============================================
// drawSpecialWeaponIcon() - 特殊兵装アイコン描画
// ============================================
function drawSpecialWeaponIcon(x: number, y: number, shipName: string, iconScale: number = 1.0) {
    const p = getP5();
    p.push();
    p.translate(x, y);
    p.scale(iconScale);
    p.translate(-x, -y);

    p.noStroke();

    if (shipName === 'bomber') {
        // ボム: 爆発の形
        p.fill(CONSOLE_COLORS.warning[0], CONSOLE_COLORS.warning[1], CONSOLE_COLORS.warning[2], 200);
        p.circle(x, y, 24);

        p.fill(CONSOLE_COLORS.warning[0], CONSOLE_COLORS.warning[1], CONSOLE_COLORS.warning[2], 150);
        p.circle(x, y, 32);

        // 爆発の放射線
        p.stroke(CONSOLE_COLORS.warning);
        p.strokeWeight(2);
        for (let i = 0; i < 8; i++) {
            const angle = (p.TWO_PI / 8) * i;
            const x1 = x + p.cos(angle) * 12;
            const y1 = y + p.sin(angle) * 12;
            const x2 = x + p.cos(angle) * 18;
            const y2 = y + p.sin(angle) * 18;
            p.line(x1, y1, x2, y2);
        }

    } else if (shipName === 'hunter') {
        // ホーミング: 追尾する弾の軌跡
        p.noStroke();
        p.fill(CONSOLE_COLORS.secondary);

        // 曲線の軌跡
        p.beginShape();
        for (let i = 0; i < 10; i++) {
            const t = i / 9;
            const cx = x - 15 + t * 20 + p.sin(t * p.PI * 2) * 8;
            const cy = y - 15 + t * 25;
            p.ellipse(cx, cy, 4 - t * 2, 4 - t * 2);
        }
        p.endShape();

        // 先端の弾
        p.fill(CONSOLE_COLORS.warning);
        p.ellipse(x + 5, y + 10, 6, 8);

    } else if (shipName === 'lancer') {
        // 荷電粒子砲: 太いビーム
        p.fill(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 200);
        p.rect(x - 6, y - 20, 12, 30, 2);

        // 内側の明るい部分
        p.fill(CONSOLE_COLORS.warning[0], CONSOLE_COLORS.warning[1], CONSOLE_COLORS.warning[2], 220);
        p.rect(x - 3, y - 20, 6, 30, 1);

        // グロー
        p.fill(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 100);
        p.rect(x - 8, y - 20, 16, 30);

        // 発射装置
        p.fill(CONSOLE_COLORS.warning);
        p.rect(x - 8, y + 10, 16, 6, 2);
    }

    p.pop();
}

// ============================================
// drawMajorPerformanceCard() - 大項目カード（機体性能）
// ============================================
function drawMajorPerformanceCard(shipCfg: ShipConfig, y: number, shipName: string) {
    const p = getP5();
    const cardX = 40;
    const cardW = p.width - 80;
    const cardH = 110;

    // 外側のカード枠
    p.fill(CONSOLE_COLORS.primary[0] * 0.15, CONSOLE_COLORS.primary[1] * 0.15, CONSOLE_COLORS.primary[2] * 0.15, 130);
    p.stroke(CONSOLE_COLORS.primary[0], CONSOLE_COLORS.primary[1], CONSOLE_COLORS.primary[2], 120);
    p.strokeWeight(1);
    p.rect(cardX, y, cardW, cardH, 4);

    // コーナーグロー
    const cornerSize = 8;
    const glowPulse = p.sin(getTitleTimer() * 0.12) * 0.4 + 0.6;
    p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 120 * glowPulse);
    p.strokeWeight(2);
    p.line(cardX, y, cardX + cornerSize, y);
    p.line(cardX, y, cardX, y + cornerSize);
    p.line(cardX + cardW, y, cardX + cardW - cornerSize, y);
    p.line(cardX + cardW, y, cardX + cardW, y + cornerSize);
    p.line(cardX, y + cardH, cardX + cornerSize, y + cardH);
    p.line(cardX, y + cardH, cardX, y + cardH - cornerSize);
    p.line(cardX + cardW, y + cardH, cardX + cardW - cornerSize, y + cardH);
    p.line(cardX + cardW, y + cardH, cardX + cardW, y + cardH - cornerSize);

    // スキャンライン効果
    drawCardScanline(cardX, y, cardW, cardH);

    // 大項目タイトル
    p.noStroke();
    p.fill(CONSOLE_COLORS.warning);
    p.textSize(12);
    p.textAlign(p.LEFT, p.TOP);
    p.text('■ 機体性能', cardX + 15, y + 12);

    // 左側：カプセル状のアイコンエリア
    const capsuleX = cardX + 10;
    const capsuleY = y + 32;
    const capsuleW = 70;
    const capsuleH = 65;
    const capsuleRadius = capsuleH / 2;

    // カプセル背景（両端が丸い）
    p.fill(CONSOLE_COLORS.primary[0] * 0.2, CONSOLE_COLORS.primary[1] * 0.2, CONSOLE_COLORS.primary[2] * 0.2, 150);
    p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 100);
    p.strokeWeight(1);
    p.rect(capsuleX, capsuleY, capsuleW, capsuleH, capsuleRadius);

    // 内側の円（アイコンエリア）
    const circleX = capsuleX + capsuleW / 2;
    const circleY = capsuleY + capsuleH / 2;
    const circleR = 25;

    // 円の背景
    p.fill(CONSOLE_COLORS.primary[0] * 0.3, CONSOLE_COLORS.primary[1] * 0.3, CONSOLE_COLORS.primary[2] * 0.3, 180);
    p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 130);
    p.strokeWeight(1.5);
    p.circle(circleX, circleY, circleR * 2);

    // グロー効果
    p.noFill();
    p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 60 * glowPulse);
    p.strokeWeight(1);
    p.circle(circleX, circleY, (circleR + 4) * 2);

    // アイコンを描画（機体のシルエット）
    drawShipPerformanceIcon(circleX, circleY, shipName, 0.7);

    // 右側：説明カード
    const descX = cardX + 90;
    const descY = y + 32;
    const descW = cardW - 100;
    const descH = 65;

    // 説明カードの背景（シンプルな単線）
    p.fill(CONSOLE_COLORS.primary[0] * 0.25, CONSOLE_COLORS.primary[1] * 0.25, CONSOLE_COLORS.primary[2] * 0.25, 140);
    p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 80);
    p.strokeWeight(1);
    p.rect(descX, descY, descW, descH, 3);

    // データ表示
    const lineH = 14;
    const labelX = descX + 10;
    const valueX = descX + descW - 50;
    let dataY = descY + 10;

    p.textSize(8);

    // 移動スピード
    p.fill(CONSOLE_COLORS.text);
    p.textAlign(p.LEFT, p.TOP);
    p.text('移動スピード', labelX, dataY);
    p.fill(CONSOLE_COLORS.secondary);
    p.textAlign(p.RIGHT, p.TOP);
    p.text((shipCfg.moveSpeed * 100) + '%', valueX + 35, dataY);
    dataY += lineH;

    // レベルアップ上限
    p.fill(CONSOLE_COLORS.text);
    p.textAlign(p.LEFT, p.TOP);
    p.text('レベルアップ上限', labelX, dataY);
    p.fill(CONSOLE_COLORS.secondary);
    p.textAlign(p.RIGHT, p.TOP);
    p.text('Lv.' + shipCfg.powerMaxLevel, valueX + 35, dataY);
    dataY += lineH;

    // レベルアップ速度
    p.fill(CONSOLE_COLORS.text);
    p.textAlign(p.LEFT, p.TOP);
    p.text('レベルアップ速度', labelX, dataY);
    p.fill(CONSOLE_COLORS.secondary);
    p.textAlign(p.RIGHT, p.TOP);
    p.text('はやい', valueX + 35, dataY);
    dataY += lineH;

    // シールド効果時間
    const shieldSec = shipCfg.shieldDuration / 60;
    p.fill(CONSOLE_COLORS.text);
    p.textAlign(p.LEFT, p.TOP);
    p.text('シールド効果時間', labelX, dataY);
    p.fill(CONSOLE_COLORS.secondary);
    p.textAlign(p.RIGHT, p.TOP);
    p.text(shieldSec.toFixed(1) + '秒', valueX + 35, dataY);
}

// ============================================
// drawShipPerformanceIcon() - 機体性能アイコン描画
// ============================================
function drawShipPerformanceIcon(x: number, y: number, _shipName: string, iconScale: number = 1.0) {
    const p = getP5();
    p.push();
    p.translate(x, y);
    p.scale(iconScale);
    p.translate(-x, -y);

    p.noStroke();

    // 機体のシルエット（シンプルな戦闘機型）
    p.fill(CONSOLE_COLORS.secondary);

    // 機体本体
    p.triangle(x - 8, y + 12, x + 8, y + 12, x, y - 15);

    // 翼
    p.triangle(x - 15, y + 5, x - 5, y + 5, x - 8, y - 3);
    p.triangle(x + 15, y + 5, x + 5, y + 5, x + 8, y - 3);

    // コックピット
    p.fill(CONSOLE_COLORS.warning);
    p.circle(x, y - 5, 6);

    // エンジン部分
    p.fill(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 150);
    p.rect(x - 3, y + 8, 6, 8, 1);

    // エンジンの光
    p.fill(CONSOLE_COLORS.warning[0], CONSOLE_COLORS.warning[1], CONSOLE_COLORS.warning[2], 200);
    p.rect(x - 2, y + 12, 4, 4, 1);

    p.pop();
}

// ============================================
// drawCardScanline() - カード内スキャンライン
// ============================================
function drawCardScanline(x: number, y: number, w: number, h: number) {
    const p = getP5();
    const progress = (getTitleTimer() * 0.3) % 1.0;
    const scanY = y + h * progress;

    p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 60);
    p.strokeWeight(1);
    p.line(x + 5, scanY, x + w - 5, scanY);

    // グロー効果
    p.stroke(CONSOLE_COLORS.secondary[0], CONSOLE_COLORS.secondary[1], CONSOLE_COLORS.secondary[2], 30);
    p.line(x + 5, scanY + 1, x + w - 5, scanY + 1);
    p.line(x + 5, scanY - 1, x + w - 5, scanY - 1);
}

// ============================================
// drawStatBar() - ステータスバー描画
// ============================================
function drawStatBar(x: number, y: number, w: number, h: number, value: number, barColor: number[]) {
    const p = getP5();
    const ratio = value / 5.0;

    // 枠
    p.noFill();
    p.stroke(barColor[0] * 0.5, barColor[1] * 0.5, barColor[2] * 0.5);
    p.strokeWeight(1);
    p.rect(x, y, w, h);

    // バー
    p.fill(barColor[0], barColor[1], barColor[2], 150);
    p.noStroke();
    p.rect(x + 2, y + 2, (w - 4) * ratio, h - 4);

    // グリッド（5分割）
    p.stroke(barColor[0] * 0.3, barColor[1] * 0.3, barColor[2] * 0.3);
    for (let i = 1; i < 5; i++) {
        const gx = x + (w / 5) * i;
        p.line(gx, y, gx, y + h);
    }
}


// ============================================
// drawDataBar() - データバー描画
// ============================================
function drawDataBar(x: number, y: number, w: number, h: number, ratio: number, barColor: number[]) {
    const p = getP5();
    // 枠
    p.noFill();
    p.stroke(barColor[0] * 0.5, barColor[1] * 0.5, barColor[2] * 0.5);
    p.strokeWeight(1);
    p.rect(x, y, w, h);

    // バー
    p.fill(barColor[0], barColor[1], barColor[2], 150);
    p.noStroke();
    p.rect(x + 1, y + 1, (w - 2) * ratio, h - 2);

    // グリッド
    p.stroke(barColor[0] * 0.3, barColor[1] * 0.3, barColor[2] * 0.3);
    for (let i = 1; i < 10; i++) {
        const gx = x + (w / 10) * i;
        p.line(gx, y, gx, y + h);
    }
}

// ============================================
// drawEnemiesGallery() - 敵ギャラリーの描画
// ============================================
function drawEnemiesGallery(index: number) {
    const p = getP5();
    const enemies: EnemyType[] = ['normal', 'tank', 'fast', 'armored', 'speedy', 'laser', 'mirror', 'boss'];
    const enemyType = enemies[index];
    const enemyCfg = ENEMY_CONFIG[enemyType];

    // 敵名（ターミナル風）
    p.textAlign(p.CENTER, p.TOP);
    p.textSize(18);
    p.fill(CONSOLE_COLORS.error);
    p.text('[ THREAT: ' + enemyType.toUpperCase() + ' ]', p.width / 2, 135);

    // ID番号
    p.textSize(10);
    p.fill(CONSOLE_COLORS.text[0], CONSOLE_COLORS.text[1], CONSOLE_COLORS.text[2], 150);
    p.text('THREAT_ID: 0x' + (index + 1).toString(16).toUpperCase().padStart(4, '0'), p.width / 2, 155);

    // 敵描画（ホログラム風）
    const size = enemyType === 'boss' ? 65 : enemyCfg.size;
    const rotation = getTitleTimer() * enemyCfg.rotSpeed;
    const hpRatio = 1.0;
    const baseColor = p.color(enemyCfg.color[0], enemyCfg.color[1], enemyCfg.color[2]);
    const damageColor = getDamageColor(baseColor, hpRatio);

    const hp = enemyType === 'boss' ? (enemyCfg as BossEnemyConfig).baseHp : (enemyCfg as BaseEnemyConfig).hp;

    const dummyEnemy: Enemy = {
        x: 0,
        y: 240,
        size: size,
        rotation: rotation,
        color: p.color(enemyCfg.color[0], enemyCfg.color[1], enemyCfg.color[2]),
        hp: hp,
        maxHp: hp,
        type: enemyType as EnemyType,
        lane: 0,
        rotSpeed: enemyCfg.rotSpeed,
        speedMult: enemyCfg.speedMult,
        score: enemyCfg.score,
        // 各タイプ別のプロパティ（描画に必要なもののみ）
        armor: true,
        armorChance: 0,
        zigzag: true,
        zigzagTimer: 0,
        zigzagDir: 1,
        zigzagInterval: 20,
        laserState: 'charging',
        laserTimer: 0,
        stopY: 200,
        chargeTime: 60,
        laserDuration: 30,
        isMirror: true,
        targetLane: 0,
        isChangingLane: false,
        laneChangeTimer: 0,
        laneChangeDelay: 30,
        isBoss: enemyType === 'boss',
        phase: 0,
        innerColor: p.color(255, 0, 0),
        // 足りないボス用プロパティを補完
        moveTimer: 0,
        moveDir: 0,
        shootTimer: 0,
        patternTimer: 0,
        currentPattern: '',
        attackTimer: 0,
        isStaying: true,
        isDescending: false,
        stayTimer: 0,
        phaseTimer: 0,
        laneChangeInterval: 120,
        baseX: 0,
        descentState: '',
        descentTargetY: 0,
        descentPauseTimer: 0,
        zigzagPhase: 0
    } as Enemy;

    p.push();
    p.translate(p.width / 2, 240);

    // ホログラムエフェクト
    const hologramAlpha = 0.8 + p.sin(getTitleTimer() * 0.1) * 0.2;
    (p.drawingContext as CanvasRenderingContext2D).globalAlpha = hologramAlpha;

    p.rotate(rotation);

    if (dummyEnemy.type === 'normal') {
        drawNormalEnemy(dummyEnemy, damageColor, hpRatio);
    } else if (dummyEnemy.type === 'tank') {
        drawTankEnemy(dummyEnemy, damageColor, hpRatio);
    } else if (dummyEnemy.type === 'fast') {
        drawFastEnemy(dummyEnemy, damageColor, hpRatio);
    } else if (dummyEnemy.type === 'armored') {
        drawArmoredEnemy(dummyEnemy, damageColor, hpRatio);
    } else if (dummyEnemy.type === 'speedy') {
        drawSpeedyEnemy(dummyEnemy, damageColor, hpRatio);
    } else if (dummyEnemy.type === 'laser') {
        drawLaserEnemy(dummyEnemy, damageColor, hpRatio);
    } else if (dummyEnemy.type === 'mirror') {
        drawMirrorEnemy(dummyEnemy, damageColor, hpRatio);
    } else if (dummyEnemy.type === 'boss') {
        drawBossEnemy(dummyEnemy, damageColor, hpRatio);
    }

    (p.drawingContext as CanvasRenderingContext2D).globalAlpha = 1.0;

    // 計測点マーカー
    p.noFill();
    p.stroke(CONSOLE_COLORS.error[0], CONSOLE_COLORS.error[1], CONSOLE_COLORS.error[2], 100);
    p.strokeWeight(1);
    p.rotate(-rotation);
    p.circle(0, 0, size * 1.8);

    p.pop();

    // データテーブル
    drawEnemyDataTable(enemyCfg, enemyType, 315);

    // 特性（ターミナル風）
    let traitText = '';
    if (enemyType === 'armored') {
        traitText = '// WARNING: Reflects projectiles';
    }
    if (enemyType === 'speedy') {
        traitText = '// CAUTION: Evasive movement pattern';
    }
    if (enemyType === 'laser') {
        traitText = '// DANGER: Laser weapon system detected';
    }
    if (enemyType === 'mirror') {
        traitText = '// ALERT: Mirrors player position';
    }
    if (enemyType === 'boss') {
        traitText = '// !! CRITICAL THREAT !!';
    }

    if (traitText) {
        p.textAlign(p.CENTER, p.TOP);
        p.fill(CONSOLE_COLORS.warning);
        p.textSize(10);
        p.text(traitText, p.width / 2, 430);
    }

    // インジケーター
    p.textAlign(p.CENTER, p.TOP);
    p.fill(CONSOLE_COLORS.text[0], CONSOLE_COLORS.text[1], CONSOLE_COLORS.text[2], 150);
    p.textSize(10);
    p.text('[RECORD ' + (index + 1) + '/' + enemies.length + ']', p.width / 2, p.height - 105);
}

// ============================================
// drawEnemyDataTable() - 敵データテーブル
// ============================================
function drawEnemyDataTable(enemyCfg: BaseEnemyConfig | BossEnemyConfig, _enemyType: EnemyType, startY: number) {
    const p = getP5();
    const lineH = 22;
    const labelX = 50;
    const valueX = 180;
    const barX = 250;
    const barW = 100;
    const barH = 10;

    p.textAlign(p.LEFT, p.CENTER);
    p.textSize(10);

    const isBoss = _enemyType === 'boss';
    const hp = isBoss ? (enemyCfg as BossEnemyConfig).baseHp : (enemyCfg as BaseEnemyConfig).hp;
    const maxHp = isBoss ? 2000 : 300;

    // HP (with bar)
    p.fill(CONSOLE_COLORS.text);
    p.text('HP:', labelX, startY);
    p.fill(CONSOLE_COLORS.error);
    p.text(hp, valueX, startY);
    drawDataBar(barX, startY - barH / 2, barW, barH, hp / maxHp, CONSOLE_COLORS.error);

    // SPEED (with bar)
    p.fill(CONSOLE_COLORS.text);
    p.text('SPEED:', labelX, startY + lineH);
    p.fill(CONSOLE_COLORS.secondary);
    p.text((enemyCfg.speedMult * 100) + '%', valueX, startY + lineH);
    drawDataBar(barX, startY + lineH - barH / 2, barW, barH, enemyCfg.speedMult / 2, CONSOLE_COLORS.secondary);

    // SCORE
    p.fill(CONSOLE_COLORS.text);
    p.text('SCORE:', labelX, startY + lineH * 2);
    p.fill(CONSOLE_COLORS.warning);
    p.text(enemyCfg.score, valueX, startY + lineH * 2);

    // APPEARS
    p.fill(CONSOLE_COLORS.text);
    p.text('APPEARS:', labelX, startY + lineH * 3);
    p.fill(CONSOLE_COLORS.text[0], CONSOLE_COLORS.text[1], CONSOLE_COLORS.text[2], 200);
    const minWave = isBoss ? 'WAVE ' + (enemyCfg as BossEnemyConfig).spawnWave : 'WAVE ' + (enemyCfg as BaseEnemyConfig).minWave + '+';
    p.text(minWave, valueX, startY + lineH * 3);
}

// ============================================
// drawStagesGallery() - ステージギャラリーの描画
// ============================================
function drawStagesGallery(index: number) {
    const p = getP5();
    const stages = CONFIG.stages.backgrounds;
    const stage = stages[index];
    if (!stage) {
        return;
    }
    const transitions = CONFIG.stages.transitions;

    // ステージ名（ターミナル風）
    p.textAlign(p.CENTER, p.TOP);
    p.textSize(18);
    p.fill(CONSOLE_COLORS.secondary);
    p.text('[ SECTOR: ' + stage.name.toUpperCase() + ' ]', p.width / 2, 135);

    // ID番号
    p.textSize(10);
    p.fill(CONSOLE_COLORS.text[0], CONSOLE_COLORS.text[1], CONSOLE_COLORS.text[2], 150);
    p.text('SECTOR_ID: 0x' + (index + 1).toString(16).toUpperCase().padStart(4, '0'), p.width / 2, 155);

    // ステージビジュアル（スキャン風枠付き）
    p.push();
    const boxX = 60;
    const boxY = 185;
    const boxW = p.width - 120;
    const boxH = 120;
    const topColor = stage.skyTop;
    const bottomColor = stage.skyBottom;

    // グラデーション
    for (let i = 0; i < boxH; i++) {
        const inter = i / boxH;
        const r = p.lerp(topColor[0], bottomColor[0], inter);
        const g = p.lerp(topColor[1], bottomColor[1], inter);
        const b = p.lerp(topColor[2], bottomColor[2], inter);
        p.stroke(r, g, b);
        p.strokeWeight(1);
        p.line(boxX, boxY + i, boxX + boxW, boxY + i);
    }

    // スキャン風の枠
    p.noFill();
    p.stroke(CONSOLE_COLORS.secondary);
    p.strokeWeight(2);
    p.rect(boxX, boxY, boxW, boxH);

    // コーナーマーカー
    const cornerSize = 10;
    p.line(boxX, boxY, boxX + cornerSize, boxY);
    p.line(boxX, boxY, boxX, boxY + cornerSize);
    p.line(boxX + boxW, boxY, boxX + boxW - cornerSize, boxY);
    p.line(boxX + boxW, boxY, boxX + boxW, boxY + cornerSize);
    p.line(boxX, boxY + boxH, boxX + cornerSize, boxY + boxH);
    p.line(boxX, boxY + boxH, boxX, boxY + boxH - cornerSize);
    p.line(boxX + boxW, boxY + boxH, boxX + boxW - cornerSize, boxY + boxH);
    p.line(boxX + boxW, boxY + boxH, boxX + boxW, boxY + boxH - cornerSize);

    p.pop();

    // 特殊効果の説明
    p.textAlign(p.CENTER, p.TOP);
    p.textSize(10);
    p.fill(CONSOLE_COLORS.text);
    const effectDescriptions: Record<string, string> = {
        'lowOrbit': '// Low orbit trajectory - Earth visible',
        'starfield': '// Dense stellar field - navigation hazard level: LOW',
        'asteroidBelt': '// Asteroid cluster detected - proceed with caution',
        'deepSpace': '// Nebula formation ahead - sensor interference possible',
        'subspace': '// Anomaly detected - space-time distortion active'
    };
    if (stage.specialEffect) {
        p.text(effectDescriptions[stage.specialEffect] || '// Unknown spatial phenomena', p.width / 2, 320);
    } else {
        p.text('// No special environmental effects detected', p.width / 2, 320);
    }

    // 出現ウェーブ
    const startWave = transitions[index];
    const endWave = index < transitions.length - 1 ? transitions[index + 1] - 1 : '∞';
    p.fill(CONSOLE_COLORS.warning);
    p.text('ACTIVE_RANGE: WAVE ' + startWave + (endWave !== '∞' ? ' - ' + endWave : '+'), p.width / 2, 340);

    // インジケーター
    p.textAlign(p.CENTER, p.TOP);
    p.fill(CONSOLE_COLORS.text[0], CONSOLE_COLORS.text[1], CONSOLE_COLORS.text[2], 150);
    p.textSize(10);
    p.text('[RECORD ' + (index + 1) + '/' + stages.length + ']', p.width / 2, p.height - 105);
}

// ============================================
// drawNavigationButtons() - ナビゲーションボタン
// ============================================
function drawNavigationButtons(category: GalleryCategory, index: number) {
    const p = getP5();
    const maxIndex = category === 'ships' ? 2 : category === 'enemies' ? 7 : CONFIG.stages.backgrounds.length - 1;
    const btnY = p.height - 75;
    const pulse = p.sin(getTitleTimer() * 0.15) * 0.3 + 0.7;

    p.textAlign(p.CENTER, p.CENTER);
    p.textSize(11);

    // PREV ボタン
    if (index > 0) {
        p.fill(CONSOLE_COLORS.primary[0] * pulse, CONSOLE_COLORS.primary[1] * pulse, CONSOLE_COLORS.primary[2] * pulse, 100);
        p.stroke(CONSOLE_COLORS.primary[0] * pulse, CONSOLE_COLORS.primary[1] * pulse, CONSOLE_COLORS.primary[2] * pulse);
        p.strokeWeight(1);
        p.rect(50, btnY, 80, 30, 3);

        p.fill(CONSOLE_COLORS.primary);
        p.noStroke();
        p.text('< PREV', 90, btnY + 15);
    }

    // インジケーター
    p.fill(CONSOLE_COLORS.text);
    p.text('[' + (index + 1) + '/' + (maxIndex + 1) + ']', p.width / 2, btnY + 15);

    // NEXT ボタン
    if (index < maxIndex) {
        p.fill(CONSOLE_COLORS.primary[0] * pulse, CONSOLE_COLORS.primary[1] * pulse, CONSOLE_COLORS.primary[2] * pulse, 100);
        p.stroke(CONSOLE_COLORS.primary[0] * pulse, CONSOLE_COLORS.primary[1] * pulse, CONSOLE_COLORS.primary[2] * pulse);
        p.strokeWeight(1);
        p.rect(p.width - 130, btnY, 80, 30, 3);

        p.fill(CONSOLE_COLORS.primary);
        p.noStroke();
        p.text('NEXT >', p.width - 90, btnY + 15);
    }
}

// ============================================
// drawExitButton() - 終了ボタン
// ============================================
function drawExitButton() {
    const p = getP5();
    const btnY = p.height - 35;
    const pulse = p.sin(getTitleTimer() * 0.1) * 0.3 + 0.7;

    p.fill(CONSOLE_COLORS.error[0] * 0.3, CONSOLE_COLORS.error[1] * 0.3, CONSOLE_COLORS.error[2] * 0.3, 80 * pulse);
    p.stroke(CONSOLE_COLORS.error[0] * pulse, CONSOLE_COLORS.error[1] * pulse, CONSOLE_COLORS.error[2] * pulse);
    p.strokeWeight(2);
    p.rect(p.width / 2 - 90, btnY, 180, 25, 3);

    p.fill(CONSOLE_COLORS.error[0] * pulse, CONSOLE_COLORS.error[1] * pulse, CONSOLE_COLORS.error[2] * pulse);
    p.noStroke();
    p.textSize(12);
    p.textAlign(p.CENTER, p.CENTER);
    p.text('[ EXIT TERMINAL ]', p.width / 2, btnY + 12);
}

// ============================================
// クリック判定関数（座標を更新）
// ============================================
export function isLeftArrowClicked() {
    const p = getP5();
    const btnY = p.height - 75;
    const index = getGalleryIndex();
    if (index === 0) {
        return false;
    }
    return p.mouseX > 50 && p.mouseX < 130 && p.mouseY > btnY && p.mouseY < btnY + 30;
}

export function isRightArrowClicked() {
    const p = getP5();
    const category = getGalleryCategory();
    const index = getGalleryIndex();
    const maxIndex = category === 'ships' ? 2 : category === 'enemies' ? 7 : CONFIG.stages.backgrounds.length - 1;
    if (index >= maxIndex) {
        return false;
    }
    const btnY = p.height - 75;
    return p.mouseX > p.width - 130 && p.mouseX < p.width - 50 && p.mouseY > btnY && p.mouseY < btnY + 30;
}

export function isBackButtonClicked() {
    const p = getP5();
    const btnY = p.height - 35;
    return p.mouseX > p.width / 2 - 90 && p.mouseX < p.width / 2 + 90 &&
           p.mouseY > btnY && p.mouseY < btnY + 25;
}

export function isCategoryTabClicked() {
    const p = getP5();
    const categories = ['ships', 'enemies', 'stages'];
    const tabY = 70;
    const tabSpacing = 110;
    const startX = 35;
    const tabWidth = 90;
    const tabHeight = 20;

    for (let i = 0; i < categories.length; i++) {
        const tabX = startX + i * tabSpacing;
        if (p.mouseX > tabX && p.mouseX < tabX + tabWidth &&
            p.mouseY > tabY - tabHeight / 2 && p.mouseY < tabY + tabHeight / 2) {
            return categories[i];
        }
    }
    return null;
}

export function isShipPageTabClicked() {
    const p = getP5();
    const tabY = 180;
    const tabW = 70;
    const tabH = 25;
    const tabSpacing = 8;
    const centerX = p.width / 2;

    // [INFO] タブ
    const infoX = centerX - tabW - tabSpacing;
    if (p.mouseX > infoX - tabW / 2 && p.mouseX < infoX + tabW / 2 &&
        p.mouseY > tabY - tabH / 2 && p.mouseY < tabY + tabH / 2) {
        return 0;
    }

    // [SPEC] タブ
    const specX = centerX;
    if (p.mouseX > specX - tabW / 2 && p.mouseX < specX + tabW / 2 &&
        p.mouseY > tabY - tabH / 2 && p.mouseY < tabY + tabH / 2) {
        return 1;
    }

    // [ABILITY] タブ
    const abilityX = centerX + tabW + tabSpacing;
    if (p.mouseX > abilityX - tabW / 2 && p.mouseX < abilityX + tabW / 2 &&
        p.mouseY > tabY - tabH / 2 && p.mouseY < tabY + tabH / 2) {
        return 2;
    }

    return null;
}
